import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Config from "../Config.json";
import { useNavigate, useParams } from "react-router-dom";
import { getArticleDetails } from "../Fetching";
import { useTranslation } from "react-i18next";

const Aitools = () => {
  const TITLE = Config.SITE_TITLE + " | AI Tools";
  const DESC = "Explore the latest AI tools, assistants, and innovations.";
  const CANONICAL = Config.SITE_DOMAIN + "/aitools";

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lng } = useParams();
  const language = lng || i18n.language || "en";

  const [filteredArticles, setFilteredArticles] = useState([]);
  const [counters, setCounters] = useState({});

  const calculateElapsedTime = (timeString) => {
    const publicationDate = new Date(timeString);
    const now = new Date();
    const diffInMs = now - publicationDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${diffInDays} days ago`;
  };

  const parseTimeToMs = (t) => {
    if (!t) return 0;
    const iso = t.replace(" ", "T");
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  // 🔹 AI Tools cikkek lekérése és rendezése
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const allArticles = await getArticleDetails(language);

        const aiToolsArticles = (allArticles || [])
          .filter((article) => article.category === 3 || article.category === "3")
          .slice()
          .sort((a, b) => parseTimeToMs(b.time) - parseTimeToMs(a.time)); // 🔹 legfrissebb előre

        setFilteredArticles(aiToolsArticles);

        // Számlálók inicializálása
        const initialCounters = {};
        aiToolsArticles.forEach((article) => {
          initialCounters[article.ArtId] = {
            likes: article.plus || 0,
            dislikes: article.minus || 0,
          };
        });
        setCounters(initialCounters);
      } catch (error) {
        console.error("❌ Error fetching AI Tools articles:", error);
      }
    };

    fetchArticles();
  }, [language]);

  const handleLike = (articleId) => {
    setCounters((prev) => ({
      ...prev,
      [articleId]: {
        likes: (prev[articleId]?.likes || 0) + 1,
        dislikes: prev[articleId]?.dislikes || 0,
      },
    }));
  };

  const handleDislike = (articleId) => {
    setCounters((prev) => ({
      ...prev,
      [articleId]: {
        likes: prev[articleId]?.likes || 0,
        dislikes: (prev[articleId]?.dislikes || 0) + 1,
      },
    }));
  };

  // 🔹 Új útvonal: /article/:artID?lang=xx
  const handleReadMore = (artId) => {
    console.log("🚀 Navigating to:", `/article/${artId}?lang=${language}`);
    navigate(`/article/${artId}?lang=${language}`);
  };

  return (
    <main className="main-content">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESC} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta
          property="og:image"
          content={`${Config.SITE_DOMAIN}/logo192.png`}
        />
      </Helmet>

      <button id="TopBtn" title="Go to top">
        &#10224;
      </button>

      <div className="container ai-container">
        <h1>AI Assistants and Chatbots for Everyday Life</h1>
        <h2>Latest Trends in Image Generation and AI Tools</h2>

        <section>
          <h2><b>{t("AiToolsTitle")}</b></h2>
          <h3><b>{t("AiToolsTitleH31")}</b></h3>
          <p><b>{t("AiToolsTitleHp1")}</b></p>
          <h3><b>{t("AiToolsTitleH32")}</b></h3>
          <p>{t("AiToolsTitleH32")}</p>
          <ul>
            <li><b>{t("AiToolsTitleLi11")}</b>{t("AiToolsTitleLi111")}</li>
            <li><b>{t("AiToolsTitleLi12")}</b>{t("AiToolsTitleLi121")}</li>
            <li><b>{t("AiToolsTitleLi13")}</b>{t("AiToolsTitleLi131")}</li>
            <li><b>{t("AiToolsTitleLi14")}</b>{t("AiToolsTitleLi141")}</li>
            <li><b>{t("AiToolsTitleLi15")}</b>{t("AiToolsTitleLi151")}</li>
          </ul>
        </section>

        {/* Cikkek megjelenítése */}
        {filteredArticles.length === 0 ? (
          <p className="text-center">No AI Tools articles available.</p>
        ) : (
          <div className="row g-3">
            {filteredArticles.map((article) => (
              <div className="col-sm-12 col-md-6 col-lg-3" key={article.ArtId}>
                <div className="card h-100">
                  <img
                    src={
                      article.img
                        ? `/articleImages/${article.img.split("/").pop()}`
                        : "/placeholder-300x200.png"
                    }
                    className="card-img-top"
                    alt={article.alt || "Article Image"}
                    onError={(e) => {
                      if (!e.target.dataset.errorHandled) {
                        e.target.dataset.errorHandled = "true";
                        e.target.src = "/placeholder-300x200.png";
                      }
                    }}
                  />

                  <div className="card-body">
                    <h5 className="card-title">{article.Title}</h5>
                    <p className="card-text text-truncate">{article.Teaser}</p>
                    <p className="text-muted">
                      Published: {calculateElapsedTime(article.time)}
                    </p>

                    <div className="d-flex justify-content-between">
                      <button
                        className="btn btn-outline-success btn-sm"
                        onClick={() => handleLike(article.ArtId)}
                      >
                        👍 {counters[article.ArtId]?.likes || 0}
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDislike(article.ArtId)}
                      >
                        👎 {counters[article.ArtId]?.dislikes || 0}
                      </button>
                    </div>

                    <button
                      onClick={() => handleReadMore(article.ArtId)}
                      className="btn btn-AIinsideLife-primary w-100 mt-2"
                    >
                      Read More...
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Aitools;
